import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { appCurrentBranding } from '../../../config/branding';
import { branding } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { AccountFacilityAddState } from '../../../interface/account-management-facility.interface';
import { AccountParameters } from '../../../interface/account-management.interface';
import { useAppDispatch } from '../../../redux/hooks';
import { fetchAllAccounts, fetchAllAccountsId } from '../../../redux/slices/AccountManagementSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
// Removed Redux role fetch; using direct API
import MyButton from '../../../shared-components/button/Button';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../shared-components/custom-input/CustomPhoneInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import getErrorMessageFromConcatenatedJSON from '../../../shared-functions/getErrorMessageFromConcatenatedJSON';
import {
  checkFileSizeValidation,
  emailValidation,
  partnerNameValidation,
  passwordValidation,
  phoneNumberValidation,
} from '../../../shared-functions/Validation';
import accountApi from '../../../utils/api/account';
import roleApi from '../../../utils/api/roles';

/**
 * Add Account function component
 * @param {any} props The component props.
 * @param {any} props.closeModal - The closeModal prop
 * @param {any} props.sidebarClosed - The sidebarClosed prop
 * @param {any} props.hideHeader - The hideHeader prop
 * @returns {any} The rendered React element.
 */
function AddFacilityAccount({
  closeModal,
  sidebarClosed,
  hideHeader,
}: {
  closeModal: (value: string) => void;
  sidebarClosed: () => void;
  hideHeader?: boolean;
}) {
  const { t } = useTranslation('account');
  const dispatch = useAppDispatch();
  const [rolesList, setRolesList] = useState<{ label: string; value: string }[]>([]);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
    notification: false,
  });
  const [emailError, setEmailError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');

  // Directly fetch roles (id + name) on mount
  useEffect(() => {
    /**
     * fetch roles for the facility account
     */
    const fetchRoles = async () => {
      try {
        // Using fetchAllRolesDropdown with empty role filter to get all roles
        const response = await roleApi.fetchAllRolesDropdown('partner');
        const options = response.data.map((r: any) => ({ label: r.name, value: r._id }));
        setRolesList(options);
      } catch (error) {
        console.error('Failed to load roles', error);
      }
    };
    fetchRoles();
  }, []);

  const [accountDetails, setAccountDetails] = useState<
    AccountFacilityAddState & { type: 'email' | 'phone' }
  >({
    name: '',
    accountName: '',
    governmentId: [],
    email: '',
    phone: '',
    roles: [],
    password: '',
    rePassword: '',
    type: 'email',
    countryCode: appCurrentBranding === branding.VOLTIE ? '1' : '966',
  });

  /**
   * Handle the change function
   * @param {string} name The field name.
   * @param {any} value The new field value.
   */
  const handleChangeFunction = (name: string, value: string[] | File[] | string | number) => {
    if (name === 'type') {
      setEmailError('');
      setPhoneError('');
      setAccountDetails((previous) => ({
        ...previous,
        type: value as 'email' | 'phone',
        email: '',
        phone: '',
      }));
    } else if (name === 'email') {
      setEmailError('');
      setAccountDetails((previous) => ({
        ...previous,
        email: value as string,
      }));
    } else if (name === 'phone') {
      setPhoneError('');
      setAccountDetails((previous) => ({
        ...previous,
        phone: value as string,
      }));
    } else if (name === 'governmentId') {
      setAccountDetails((previous) => ({
        ...previous,
        governmentId: [...previous.governmentId, ...(value as File[])],
      }));
    } else if (name === 'roles') {
      setAccountDetails((previous) => ({
        ...previous,
        roles: value ? [value as string] : [],
      }));
    } else {
      setAccountDetails((previous) => ({
        ...previous,
        [name]: value,
      }));
    }
  };

  const selectedRole = rolesList.find((role) => role.value === accountDetails.roles[0]);

  /**
   * Password field error message: required, then length, then complexity
   * @returns {string} the error message
   */
  const getPasswordErrorMessage = () => {
    if (!accountDetails.password) return t('passwordRequired');
    if (accountDetails.password.length < 12) return t('passwordLengthMismatch');
    return t('passwordRequiredOrInvalid');
  };

  /**
   * Confirm password field error message: required, then length, then match with password
   * @returns {string} the error message
   */
  const getRePasswordErrorMessage = () => {
    if (!accountDetails.rePassword) return t('reEnterPasswordRequired');
    if (accountDetails.rePassword.length < 12) return t('reEnterPasswordLengthMismatch');
    if (accountDetails.rePassword !== accountDetails.password) return t('passwordsMustMatch');
    return t('reEnterPasswordRequiredOrInvalid');
  };

  /**
   * Handle the submit function
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    setEmailError('');
    setPhoneError('');
    let errorMessage = false;
    if (
      !partnerNameValidation(1, 100, accountDetails.accountName) ||
      !partnerNameValidation(1, 100, accountDetails.name) ||
      (accountDetails.governmentId.length > 0 &&
        !checkFileSizeValidation(accountDetails.governmentId, 1, 1)) ||
      (!accountDetails.email && !accountDetails.phone) ||
      (accountDetails.email && !emailValidation(accountDetails.email)) ||
      (accountDetails.phone && !phoneNumberValidation(accountDetails.phone)) ||
      !passwordValidation(accountDetails.password) ||
      accountDetails.rePassword !== accountDetails.password
    ) {
      errorMessage = true;
    }
    if (!errorMessage) {
      setDisabled(true);
      const request: AccountParameters = {
        name: accountDetails.name,
        userName: accountDetails.name,
        password: accountDetails.password,
        accountName: accountDetails.accountName,
        roles: selectedRole?.value ? [selectedRole.value] : accountDetails.roles,
      };
      const [firstGovernmentId] = accountDetails.governmentId;
      if (firstGovernmentId) {
        request.governmentId = firstGovernmentId;
      }
      if (accountDetails.phone) {
        request.phoneNumber = accountDetails.phone;
        request.countryCode = accountDetails.countryCode;
      }
      if (accountDetails.email) {
        request.email = accountDetails.email;
      }
      accountApi
        .createAccounts(request)
        .then((response: any) => {
          getErrorMessageFromConcatenatedJSON(response.data);
          setDisabled(true);
          setModalView({
            status: true,
            title: '',
            header: false,
            rightCorner: '',
            notification: true,
            content: (
              <SuccessNotification
                title={t('accountAddedSuccessfully')}
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={() => {
                  setModalView((previous) => ({ ...previous, status: false }));
                  closeModal(t('accountAddedSuccessfully'));
                  sidebarClosed();
                }}
              />
            ),
          });
          dispatch(
            fetchAllAccounts({
              page: DefaultApiParameters.page,
              limit: DefaultApiParameters.limit,
            }),
          );
          dispatch(fetchAllAccountsId());
        })
        .catch((error) => {
          setDisabled(false);
          const apiMessage =
            error?.response?.data?.message || error?.response?.data?.error || error?.message || '';
          const lowerMessage = String(apiMessage).toLowerCase();

          if (
            lowerMessage.includes('email') ||
            (accountDetails.type === 'email' &&
              (lowerMessage.includes('already exist') ||
                lowerMessage.includes('already registered') ||
                lowerMessage.includes('already in use') ||
                lowerMessage.includes('duplicate')))
          ) {
            setEmailError(t('emailAlreadyExists'));
          } else if (
            lowerMessage.includes('phone') ||
            lowerMessage.includes('mobile') ||
            (accountDetails.type === 'phone' &&
              (lowerMessage.includes('already exist') ||
                lowerMessage.includes('already registered') ||
                lowerMessage.includes('already in use') ||
                lowerMessage.includes('duplicate')))
          ) {
            setPhoneError(t('phoneNumberAlreadyExists'));
          }

          dispatch(
            setNotificationMessage({
              message: apiMessage,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Handle the government id remove function
   * @param {any} name The file name.
   * @param {any} url The file URL.
   * @param {any} indexToRemove The index of the file to remove.
   * @param {any} type The type of file ('newFile' | 'existingFile').
   */
  const handleRemoveFile = (
    name: string,
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => {
    if (type === 'newFile') {
      const governmentId = accountDetails?.governmentId;
      governmentId.splice(indexToRemove, 1);
      setAccountDetails((previous) => ({
        ...previous,
        governmentId,
      }));
    }
  };

  return (
    <div className="account-form">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      {!hideHeader && (
        <div className="form-header">
          <h3>{t('addFacility')}</h3>
        </div>
      )}
      <div className="form-body">
        <div className="form-row">
          <div className="form-field">
            <CustomInput
              name="accountName"
              inputLabel={t('accountName')}
              type="text"
              placeholder={t('enterAccountName')}
              value={accountDetails.accountName}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={showFormErrors && !partnerNameValidation(1, 100, accountDetails.accountName)}
              errorMessage={
                !accountDetails.accountName
                  ? t('accountNameRequired')
                  : t('accountNameInvalid', { defaultValue: 'Invalid account name format' })
              }
              characterLimit={100}
            />
          </div>
          <div className="form-field">
            <CustomInput
              name="name"
              inputLabel={t('userName')}
              type="text"
              placeholder={t('enterUserName')}
              value={accountDetails.name}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={showFormErrors && !partnerNameValidation(1, 100, accountDetails.name)}
              errorMessage={
                !accountDetails.name
                  ? t('userNameRequired')
                  : t('userNameInvalid', { defaultValue: 'Invalid user name format' })
              }
              characterLimit={100}
            />
          </div>
        </div>

        <div className="form-field">
          <CustomFileInput
            name="governmentId"
            inputLabel={t('document')}
            placeholder={t('uploadHere')}
            selectedFiles={accountDetails.governmentId}
            existingName="governmentIdOld"
            onChangeValue={handleChangeFunction}
            unselectHandler={handleRemoveFile}
            acceptedFileType="application/pdf"
            maxFileSizeMB={1}
            maxFileCount={1}
            tooltipText={t(
              'governmentIdTooltip',
              'Government ID or registration document. Supported: PDF (Max file size: 1 MB)',
            )}
          />
        </div>
        <div className="radio-button-wrap">
          <RadioButton
            name="type"
            value={accountDetails.type}
            list={[
              { label: t('email'), value: 'email' },
              { label: t('phone'), value: 'phone' },
            ]}
            onChangeValue={handleChangeFunction}
          />
        </div>

        <div className="radio-button-wrap">
          {accountDetails.type === 'email' && (
            <div className="form-field">
              <CustomInput
                name="email"
                inputLabel={t('emailId')}
                type="email"
                placeholder={t('enterEmail')}
                value={accountDetails.email}
                onChangeValue={handleChangeFunction}
                valueRequired
                error={
                  (showFormErrors &&
                    (accountDetails.email === '' || !emailValidation(accountDetails.email))) ||
                  !!emailError
                }
                errorMessage={emailError || t('emailRequiredOrInvalid')}
              />
            </div>
          )}
          {accountDetails.type === 'phone' && (
            <div className="form-field">
              <CustomPhoneInput
                name="phone"
                inputLabel={t('phoneNumber')}
                type="number"
                placeholder={t('enterPhoneNumber')}
                value={accountDetails.phone}
                value1={accountDetails.countryCode}
                onChangeValue={handleChangeFunction}
                valueRequired
                error={
                  (showFormErrors &&
                    (accountDetails.phone === '' ||
                      !phoneNumberValidation(accountDetails.phone))) ||
                  !!phoneError
                }
                errorMessage={phoneError || t('phoneRequiredOrInvalid')}
              />
            </div>
          )}
        </div>
        <SelectField
          name="roles"
          inputLabel={t('role')}
          selectedValue={accountDetails.roles[0] || ''}
          options={rolesList}
          placeholder={t('selectRole')}
          onChangeValue={handleChangeFunction}
          valueRequired
          error={showFormErrors && accountDetails.roles.length === 0}
          errorMessage={t('roleRequired')}
        />
        <div className="form-row">
          <div className="form-field">
            <CustomInput
              name="password"
              inputLabel={t('setPassword')}
              type="password"
              placeholder={t('enterPassword')}
              value={accountDetails.password}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={showFormErrors && !passwordValidation(accountDetails.password)}
              errorMessage={getPasswordErrorMessage()}
            />
          </div>
          <div className="form-field">
            <CustomInput
              name="rePassword"
              inputLabel={t('reEnterPassword')}
              type="password"
              placeholder={t('enterPassword')}
              value={accountDetails.rePassword}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={
                showFormErrors &&
                (!passwordValidation(accountDetails.rePassword) ||
                  accountDetails.rePassword !== accountDetails.password)
              }
              errorMessage={getRePasswordErrorMessage()}
            />
          </div>
        </div>
      </div>
      <div className="form-bottom-button">
        <div className="table-button-item">
          <MyButton
            label={t('cancel')}
            buttonType="outline"
            onClickFunc={() => {
              closeModal('');
              sidebarClosed();
            }}
          />
        </div>
        <div className="table-button-item">
          <MyButton
            label={t('submit')}
            buttonType="submit"
            loading={disabled}
            disableButton={disabled}
            onClickFunc={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}

AddFacilityAccount.defaultProps = {
  hideHeader: false,
};

export default AddFacilityAccount;
